import { CANTRATE_ID } from '@webapp/common/resources/constants';
import type { QuestionModel } from '@webapp/common/resources/entities';
import type { QuestionEntity } from '@webapp/common/resources/entities/question';

import type { QuestionContext } from '@webapp/survey/src/machine/question-machine';

export enum DropdownType {
    DIFFERENT = 'different',
    COMMON = 'common',
}

export enum DropdownListType {
    SINGLE = 'single',
    MULTI = 'multi',
}

export enum QuestionType {
    DEFAULT = 0, // during creation, type select

    ONE_OF_LIST = 4,
    FEW_OF_LIST = 6,

    SELECT_ONE_IMAGE = 24,
    SELECT_FEW_IMAGE = 25,

    DROPDOWN_LIST = 15,
    SCALE = 9,
    STAR = 14,
    SMILE = 20,
    DISTRIBUTE_SCALE = 27,
    NPS = 23,
    RANK = 18,

    MATRIX_SINGLE_ANSWER = 10,
    MATRIX_FEW_ANSWERS = 11,
    MATRIX_TEXT_ANSWER = 12,
    MATRIX_DROPDOWN_LIST = 13,
    MATRIX_RATING = 26,

    SEM_DIFF = 32,

    SHORT_TEXT = 1,
    LONG_TEXT = 2,
    NUMBER = 3,
    GROUP_FREE_ANSWERS = 33,

    FILE_UPLOAD = 16,
    RESIDENCE = 8,

    NESTED_LIST = 37,

    NAME = 28,
    EMAIL = 21,
    DATETIME = 22,
    PHONE = 29,
    AGREEMENT = 34,

    TEXT_BLOCK = 17,

    TEST_ONE_OF_LIST = 35,
    TEST_FEW_OF_LIST = 36,
    TEST_TEXT = 38,
    TEST_RANK = 39,
    CSI = 40,

    AB_TESTING = 41,
    TEXT_PERCEPTION = 42,
    PAIR_COMPARISON = 43,
    FIRST_CLICK = 44,
    CARD_SORTING = 45,
    MAX_DIFF = 46,
    FIVE_SEC_TEST = 48,
    KANO = 47,
    VIDEO_TEST = 49,
    FIGMA_TEST = 50,
    CONJOINT = 51,
}

export const TYPES_NAMES: { [type: string]: string } = {
    [QuestionType.ONE_OF_LIST]: 'Выбор одного',
    [QuestionType.FEW_OF_LIST]: 'Выбор нескольких',
    [QuestionType.SELECT_ONE_IMAGE]: 'Одно изображение',
    [QuestionType.SELECT_FEW_IMAGE]: 'Несколько изображений',
    [QuestionType.DROPDOWN_LIST]: 'Выпадающий список',
    [QuestionType.SCALE]: 'Шкала Лайкерта',
    [QuestionType.STAR]: 'Звезды',
    [QuestionType.SMILE]: 'Смайлы',
    [QuestionType.DISTRIBUTE_SCALE]: 'Распределительная шкала',
    [QuestionType.NPS]: 'NPS',
    [QuestionType.CSI]: 'CSI',
    [QuestionType.RANK]: 'Ранжирование',
    [QuestionType.MATRIX_FEW_ANSWERS]: 'Выбор нескольких',
    [QuestionType.MATRIX_SINGLE_ANSWER]: 'Выбор одного',
    [QuestionType.MATRIX_RATING]: 'Рейтинг',
    [QuestionType.MATRIX_TEXT_ANSWER]: 'Текст',
    [QuestionType.MATRIX_DROPDOWN_LIST]: 'Выпадающий список',
    [QuestionType.SEM_DIFF]: 'Семантический дифференциал',
    [QuestionType.SHORT_TEXT]: 'Короткий текст',
    [QuestionType.LONG_TEXT]: 'Длинный текст',
    [QuestionType.NUMBER]: 'Число',
    [QuestionType.GROUP_FREE_ANSWERS]: 'Группа ответов',
    [QuestionType.FILE_UPLOAD]: 'Загрузка файла',
    [QuestionType.RESIDENCE]: 'Место жительства',
    [QuestionType.NESTED_LIST]: 'Вложенные списки',
    [QuestionType.NAME]: 'Имя',
    [QuestionType.EMAIL]: 'E-mail',
    [QuestionType.DATETIME]: 'Дата/время',
    [QuestionType.PHONE]: 'Телефон',
    [QuestionType.AGREEMENT]: 'Галочка согласие',
    [QuestionType.TEXT_BLOCK]: 'Контент блок',
    [QuestionType.TEST_ONE_OF_LIST]: 'Выбор одного',
    [QuestionType.TEST_FEW_OF_LIST]: 'Выбор нескольких',
    [QuestionType.TEST_TEXT]: 'Свободный ответ',
    [QuestionType.TEST_RANK]: 'Последовательность',
    [QuestionType.AB_TESTING]: 'A/B тест',
    [QuestionType.TEXT_PERCEPTION]: 'Восприятие текста',
    [QuestionType.PAIR_COMPARISON]: 'Парное сравнение',
    [QuestionType.FIRST_CLICK]: 'Тест первого клика',
    [QuestionType.CARD_SORTING]: 'Карточная сортировка',
    [QuestionType.MAX_DIFF]: 'MaxDiff',
    [QuestionType.FIVE_SEC_TEST]: '5-секундный тест',
    [QuestionType.KANO]: 'Метод Кано',
    [QuestionType.VIDEO_TEST]: 'Тест видео',
    [QuestionType.FIGMA_TEST]: 'Figma',
    [QuestionType.CONJOINT]: 'Conjoint-анализ',
};

export enum AnswerType {
    SELF = 1,
    RADIO = 2,
    CHECKBOX = 3,
    SELECT = 4,
    TEXT = 5,
}

export enum ResidenceType {
    COUNTRY = 'COUNTRY',
    REGION = 'REGION',
    CITY = 'CITY',
}

export const RESIDENCE_TYPE_LABEL = {
    [ResidenceType.COUNTRY]: 'Страна',
    [ResidenceType.REGION]: 'Регион',
    [ResidenceType.CITY]: 'Город',
};

export enum LogicType {
    ALWAYS = 0,
    SELECT_VARIANTS = 1,
    NOT_SELECT_VARIANTS = 2,
    SKIP = 3,
    TYPE_RESPONSE_QUOTA = 4,
    TYPE_QUESTION_QUOTA = 5,
    LINK_PARAM = 6,
    SELECT_ONLY_VARIANTS = 7,
    SELECT_COUNT = 8,
    CANT_RATE = 9,
}

export enum LinkParamsLogicType {
    EQUAL = 1,
    NOT_EQUAL = 2,
}

export enum LogicBoolFunc {
    OR = 'OR',
    AND = 'AND',
}

export enum LogicTransition {
    REGULAR_COMPLETE = 0,
    PAGE = 1,
    QUESTION = 2,
    DISQUAL = 3,
    PERSONAL_COMPLETE = 5,
    QUESTIONS = 6,
    PAGES = 7,
    ANSWERS_HIDE = 8,
    FINISH_SURVEY = 9,
}

export enum LogicTransitionVariant {
    AFTER_RULES = 0,
    AFTER_QUESTION = 1,
}

export enum LogicAction {
    SHOW = 'SHOW',
    HIDE = 'HIDE',
    ANSWERS_HIDE = 'ANSWERS_HIDE',
}

export enum LogicTransitionType {
    TO_SURVEY = 0,
    REDIRECT_TO_WEBSITE = 1,
    TEXT = 2,
    STANDART = 3,
}

export enum SurveyStatus {
    STATUS_JUST_START = 0,
    STATUS_START = 1,
    STATUS_STOP = 2,
    STATUS_DISQUAL = 3,
    STATUS_DELETED = 4,
}

export enum CompletePageType {
    SUCCESS_TEXT = 'SUCCESS_TEXT',
    REDIRECT_TO_WEBSITE = 'REDIRECT_TO_WEBSITE',
    TO_SURVEY = 'TO_SURVEY',
    SURVEY_RESULTS = 'SURVEY_RESULTS',
    TEST_RESULTS = 'TEST_RESULTS',
}

export enum WelcomePageType {
    NOT = 'NOT',
    TO_SURVEY_PAGE = 'TO_SURVEY_PAGE',
    TO_SINGLE_PAGE = 'TO_SINGLE_PAGE',
}

export enum PasswordAccessType {
    NOT = 'NOT',
    WITH_PASSWORD = 'WITH_PASSWORD',
    WITH_INDIVIDUAL_PASSWORD = 'WITH_INDIVIDUAL_PASSWORD',
}

export enum RecipientPhoneAuthType {
    SMS = 'SMS',
    SMS_AND_PHONE = 'SMS_AND_PHONE',
}

export enum IndicatorPosition {
    TOP = 'TOP',
    BOTTOM = 'BOTTOM',
}

export enum CurrentSection {
    AUTH = 'auth',
    START = 'start',
    BUNDLES = 'bundles',
    FINISH = 'finish',
    RESULTS = 'results',
    DISABLED = 'disabled',
    TEST_RESULT = 'testResult',
    BLOCKED = 'blocked',
}

export const QuestionsWithClickReply = new Set([
    QuestionType.ONE_OF_LIST,
    QuestionType.SELECT_ONE_IMAGE,
    QuestionType.DROPDOWN_LIST,
    QuestionType.STAR,
    QuestionType.SCALE,
    QuestionType.SMILE,
    QuestionType.TEST_ONE_OF_LIST,
    QuestionType.NPS,
    QuestionType.PAIR_COMPARISON,
]);

export const checkIsQTypeWithClickReply = (t: QuestionType): boolean => QuestionsWithClickReply.has(t);

export const checkIsQWithClickReply = (
    question: Pick<QuestionContext, 'type' | 'params' | 'clicks'>,
    event?: Record<string, any>,
): boolean => {
    if (question.type === QuestionType.DROPDOWN_LIST && question.params.dropdownType === 'multi') {
        return false;
    }
    if (
        question.type === QuestionType.FIRST_CLICK &&
        (event?.value === CANTRATE_ID ||
            question.params.clickCount === 1 ||
            question.params.clickCount === question.clicks.length)
    ) {
        return true;
    }
    return QuestionsWithClickReply.has(question.type);
};

export const QuestionWithoutHeader = new Set([QuestionType.AGREEMENT]);

export const TypesWithScore = new Set([
    QuestionType.TEST_ONE_OF_LIST,
    QuestionType.TEST_FEW_OF_LIST,
    QuestionType.TEST_TEXT,
    QuestionType.TEST_RANK,
]);

export enum TestCheckStatus {
    NOT_CHECKED = 'NotChecked',
    VALID = 'valid',
    INVALID = 'invalid',
}

export const HIDDEN_ON_PRINT = new Set([
    QuestionType.MATRIX_DROPDOWN_LIST,
    QuestionType.MATRIX_TEXT_ANSWER,
    QuestionType.FILE_UPLOAD,
    QuestionType.GROUP_FREE_ANSWERS,
    QuestionType.NUMBER,
    QuestionType.NAME,
    QuestionType.EMAIL,
    QuestionType.DATETIME,
    QuestionType.PHONE,
]);

export const hideOnPrint = (t: QuestionType): boolean => HIDDEN_ON_PRINT.has(t);

const fontUrlTpl =
    'https://fonts.googleapis.com/css?family=${family}:ital,wght@0,100;0,300;0,400;0,500;0,700;1,100;1,300;1,400;1,500;1,700&display=swap&subset=cyrillic';

const makeUrl = (family: string): string => fontUrlTpl.replace(/ /gi, '+').replace('${family}', family);

export const FONTS = {
    Montserrat: makeUrl('Montserrat'),
    Arial: null,
    Georgia: null,
    Helvetica: null,
    Tahoma: null,
    'Times New Roman': null,
    'Trebuchet MS': null,
    Verdana: null,
    Lora: makeUrl('Lora'),
    'PT Serif Caption': makeUrl('PT Serif Caption'),
    'PT Serif': makeUrl('PT Serif'),
    'EB Garamond': makeUrl('EB Garamond'),
    Roboto: makeUrl('Roboto'),
};

export const QuestionsWithImages = new Set([QuestionType.SELECT_FEW_IMAGE, QuestionType.SELECT_ONE_IMAGE]);

export const QuestionsWithMatrix = new Set([
    QuestionType.MATRIX_SINGLE_ANSWER,
    QuestionType.MATRIX_FEW_ANSWERS,
    QuestionType.MATRIX_DROPDOWN_LIST,
    QuestionType.MATRIX_TEXT_ANSWER,
    QuestionType.MATRIX_RATING,
]);

export const withImages = (t: QuestionType): boolean => QuestionsWithImages.has(t);

export const filterWithImages = (qs: Array<QuestionModel | QuestionEntity>): Array<QuestionModel | QuestionEntity> =>
    qs.filter(({ type }) => withImages(type));

export enum DisallowRefillingType {
    ALL_TIME = 'ALL_TIME',
    PERIOD = 'PERIOD',
}

export enum DisallowRefillingTimeType {
    HOUR = 'HOUR',
    DAY = 'DAY',
}

export const QuestionsWithQuota = new Set([
    QuestionType.ONE_OF_LIST,
    QuestionType.FEW_OF_LIST,
    QuestionType.DROPDOWN_LIST,
]);
